/**
 * Revision de coherencia del arbol.
 *
 * Regla de oro: esto AVISA, no bloquea. La realidad de una familia es
 * irregular a menudo, y lo que parece un error puede ser un dato correcto:
 * un hijo que lleva el apellido de la madre, una persona de la que solo se
 * sabe el apellido, un matrimonio entre primos. Asi que cada incidencia se
 * marca y se deja que la persona decida.
 *
 * Lo unico que se trata como error de verdad es lo que el formato no puede
 * representar (una referencia a algo que no existe) o lo imposible por
 * definicion (alguien antepasado de si mismo).
 */
import { calcularGeneraciones } from "./generaciones.js";
import type { Indice } from "./grafo.js";
import { anoDe, apellidoPrimario, eventoDe, nombreCompleto, nombrePrincipal } from "./model.js";

export type Severidad = "error" | "aviso" | "info";

export interface Incidencia {
  /** Codigo estable, para poder filtrar y silenciar por tipo. */
  codigo: string;
  severidad: Severidad;
  mensaje: string;
  /** Handles de las personas implicadas. */
  personas: string[];
  /** Handles de las familias implicadas. */
  familias: string[];
}

/** Edad minima para ser progenitor por debajo de la cual se avisa. */
const EDAD_MINIMA_PROGENITOR = 12;
/** Edad por encima de la cual se avisa si una mujer tiene un hijo. */
const EDAD_MAXIMA_MADRE = 60;
/** Meses de margen tras la muerte del padre antes de avisar. */
const ANOS_MARGEN_POSTUMO = 1;

export function validar(ix: Indice): Incidencia[] {
  const out: Incidencia[] = [];
  const arbol = ix.arbol;
  const nom = (h: string): string => {
    const p = arbol.personas.get(h);
    return p ? `${nombreCompleto(p)} (${p.id ?? h})` : h;
  };

  const anade = (
    codigo: string,
    severidad: Severidad,
    mensaje: string,
    personas: string[] = [],
    familias: string[] = [],
  ): void => {
    out.push({ codigo, severidad, mensaje, personas, familias });
  };

  // --- referencias que no apuntan a nada ---------------------------------
  for (const f of arbol.familias.values()) {
    const id = f.id ?? f.handle;
    for (const [rol, h] of [
      ["padre", f.padre],
      ["madre", f.madre],
    ] as const) {
      if (h && !arbol.personas.has(h)) {
        anade("referencia-rota", "error", `la familia ${id} apunta como ${rol} a alguien que no existe`, [], [f.handle]);
      }
    }
    for (const r of f.hijos) {
      if (!arbol.personas.has(r.hlink)) {
        anade("referencia-rota", "error", `la familia ${id} apunta como hijo a alguien que no existe`, [], [f.handle]);
      }
    }
    for (const r of f.eventos) {
      if (!arbol.eventos.has(r.hlink)) {
        anade("referencia-rota", "error", `la familia ${id} apunta a un evento que no existe`, [], [f.handle]);
      }
    }
  }
  for (const p of arbol.personas.values()) {
    const id = p.id ?? p.handle;
    for (const h of [...p.childof, ...p.parentin]) {
      if (!arbol.familias.has(h)) {
        anade("referencia-rota", "error", `${id} apunta a una familia que no existe`, [p.handle]);
      }
    }
    for (const r of p.eventos) {
      if (!arbol.eventos.has(r.hlink)) {
        anade("referencia-rota", "error", `${id} apunta a un evento que no existe`, [p.handle]);
      }
    }
  }
  for (const e of arbol.eventos.values()) {
    if (e.lugar && !arbol.lugares.has(e.lugar)) {
      anade("referencia-rota", "error", `el evento ${e.id ?? e.handle} apunta a un lugar que no existe`);
    }
  }

  // --- las tres relaciones redundantes deben coincidir -------------------
  // childof / parentin en la persona y father / mother / childref en la
  // familia dicen lo mismo tres veces. Si se descuadran, Gramps se comporta
  // de forma impredecible.
  for (const p of arbol.personas.values()) {
    for (const fh of p.childof) {
      const f = arbol.familias.get(fh);
      if (f && !f.hijos.some((r) => r.hlink === p.handle)) {
        anade(
          "redundancia",
          "error",
          `${nom(p.handle)} dice ser hija de la familia ${f.id ?? fh}, pero esa familia no la lista como hija`,
          [p.handle],
          [fh],
        );
      }
    }
    for (const fh of p.parentin) {
      const f = arbol.familias.get(fh);
      if (f && f.padre !== p.handle && f.madre !== p.handle) {
        anade(
          "redundancia",
          "error",
          `${nom(p.handle)} dice ser progenitora de la familia ${f.id ?? fh}, pero esa familia no la tiene como padre ni madre`,
          [p.handle],
          [fh],
        );
      }
    }
  }
  for (const f of arbol.familias.values()) {
    for (const r of f.hijos) {
      const p = arbol.personas.get(r.hlink);
      if (p && !p.childof.includes(f.handle)) {
        anade(
          "redundancia",
          "error",
          `la familia ${f.id ?? f.handle} lista como hija a ${nom(r.hlink)}, que no lo declara`,
          [r.hlink],
          [f.handle],
        );
      }
    }
    for (const h of [f.padre, f.madre]) {
      if (!h) continue;
      const p = arbol.personas.get(h);
      if (p && !p.parentin.includes(f.handle)) {
        anade(
          "redundancia",
          "error",
          `la familia ${f.id ?? f.handle} tiene como progenitor a ${nom(h)}, que no lo declara`,
          [h],
          [f.handle],
        );
      }
    }
  }

  // --- ciclos de descendencia -------------------------------------------
  const gen = calcularGeneraciones(ix, nom);
  for (const a of gen.avisos) {
    anade("generaciones", a.includes("ciclo") ? "error" : "aviso", a);
  }

  // --- sexo frente al rol en la familia ---------------------------------
  for (const f of arbol.familias.values()) {
    const id = f.id ?? f.handle;
    if (f.padre) {
      const p = arbol.personas.get(f.padre);
      if (p?.sexo === "F") {
        anade("sexo-rol", "aviso", `${nom(f.padre)} figura como padre en ${id} pero su sexo es femenino`, [f.padre], [f.handle]);
      }
    }
    if (f.madre) {
      const m = arbol.personas.get(f.madre);
      if (m?.sexo === "M") {
        anade("sexo-rol", "aviso", `${nom(f.madre)} figura como madre en ${id} pero su sexo es masculino`, [f.madre], [f.handle]);
      }
    }
  }

  // --- hijos en mas de una familia --------------------------------------
  for (const h of ix.todas()) {
    const fams = ix.familiasComoHijo(h);
    if (fams.length > 1) {
      anade(
        "hijo-varias-familias",
        "aviso",
        `${nom(h)} figura como hija en ${fams.length} familias (${fams
          .map((f) => arbol.familias.get(f)?.id ?? f)
          .join(", ")}). Puede ser correcto si una es la familia adoptiva, pero conviene revisarlo`,
        [h],
        fams,
      );
    }
  }

  // --- apellidos --------------------------------------------------------
  // Convencion aplicada: primer apellido del padre, segundo de la madre.
  // Se avisa solo cuando los dos datos existen: si falta un apellido, no hay
  // nada que comparar y no es un error.
  const ap1 = (h: string): string | undefined => {
    const p = arbol.personas.get(h);
    if (!p) return undefined;
    const a = apellidoPrimario(nombrePrincipal(p));
    return a?.texto.trim() || undefined;
  };
  const ap2 = (h: string): string | undefined => {
    const p = arbol.personas.get(h);
    if (!p) return undefined;
    const n = nombrePrincipal(p);
    const prim = apellidoPrimario(n);
    const otro = n?.apellidos.find((x) => x !== prim);
    return otro?.texto.trim() || undefined;
  };

  for (const f of arbol.familias.values()) {
    const id = f.id ?? f.handle;
    for (const r of f.hijos) {
      const hijo = r.hlink;
      if (!arbol.personas.has(hijo)) continue;
      // Un hijo adoptado no tiene por que llevar el apellido: no se avisa.
      const adoptado = r.frel === "Adopted" || r.mrel === "Adopted";
      if (adoptado) continue;

      if (f.padre) {
        const a = ap1(hijo);
        const b = ap1(f.padre);
        if (a && b && a !== b) {
          anade(
            "apellido-paterno",
            "aviso",
            `el primer apellido de ${nom(hijo)} es "${a}" pero el de su padre ${nom(f.padre)} es "${b}" (familia ${id})`,
            [hijo, f.padre],
            [f.handle],
          );
        }
      }
      if (f.madre) {
        const a = ap2(hijo);
        const b = ap1(f.madre);
        if (a && b && a !== b) {
          anade(
            "apellido-materno",
            "aviso",
            `el segundo apellido de ${nom(hijo)} es "${a}" pero el primero de su madre ${nom(f.madre)} es "${b}" (familia ${id})`,
            [hijo, f.madre],
            [f.handle],
          );
        }
      }
    }

    // Hermanos completos deberian coincidir en los dos apellidos.
    const hijos = f.hijos.map((r) => r.hlink).filter((h) => arbol.personas.has(h));
    for (let i = 1; i < hijos.length; i++) {
      const x = hijos[0] as string;
      const y = hijos[i] as string;
      if (ix.claseHermandad(x, y) !== "completos") continue;
      const px = [ap1(x), ap2(x)];
      const py = [ap1(y), ap2(y)];
      for (let k = 0; k < 2; k++) {
        const a = px[k];
        const b = py[k];
        if (a && b && a !== b) {
          anade(
            "apellido-hermanos",
            "aviso",
            `${nom(x)} y ${nom(y)} son hermanos completos pero su ${k === 0 ? "primer" : "segundo"} apellido no coincide ("${a}" frente a "${b}")`,
            [x, y],
            [f.handle],
          );
          break;
        }
      }
    }
  }

  // --- fechas imposibles ------------------------------------------------
  const nac = (h: string): number | undefined => {
    const p = arbol.personas.get(h);
    if (!p) return undefined;
    return anoDe(eventoDe(arbol, p, "Birth")?.fecha);
  };
  const mue = (h: string): number | undefined => {
    const p = arbol.personas.get(h);
    if (!p) return undefined;
    return anoDe(eventoDe(arbol, p, "Death")?.fecha);
  };

  for (const h of ix.todas()) {
    const n = nac(h);
    const m = mue(h);
    if (n !== undefined && m !== undefined && m < n) {
      anade("fecha-muerte-antes", "aviso", `${nom(h)} consta fallecida en ${m}, antes de nacer en ${n}`, [h]);
    }
  }

  for (const f of arbol.familias.values()) {
    for (const r of f.hijos) {
      const hijo = r.hlink;
      const nh = nac(hijo);
      if (nh === undefined) continue;
      for (const [rol, prog] of [
        ["padre", f.padre],
        ["madre", f.madre],
      ] as const) {
        if (!prog) continue;
        const np = nac(prog);
        if (np !== undefined) {
          if (nh < np) {
            anade(
              "fecha-hijo-antes",
              "aviso",
              `${nom(hijo)} nació en ${nh}, antes que su ${rol} ${nom(prog)} en ${np}`,
              [hijo, prog],
              [f.handle],
            );
          } else if (nh - np < EDAD_MINIMA_PROGENITOR) {
            anade(
              "fecha-progenitor-joven",
              "aviso",
              `${nom(prog)} tendría ${nh - np} años al nacer su ${rol === "padre" ? "hijo" : "hijo"} ${nom(hijo)}`,
              [hijo, prog],
              [f.handle],
            );
          } else if (rol === "madre" && nh - np > EDAD_MAXIMA_MADRE) {
            anade(
              "fecha-madre-mayor",
              "aviso",
              `${nom(prog)} tendría ${nh - np} años al nacer ${nom(hijo)}`,
              [hijo, prog],
              [f.handle],
            );
          }
        }
        const mp = mue(prog);
        if (mp !== undefined && nh > mp + ANOS_MARGEN_POSTUMO && rol === "padre") {
          anade(
            "fecha-postumo",
            "aviso",
            `${nom(hijo)} nació en ${nh}, más de un año después de fallecer su padre ${nom(prog)} en ${mp}`,
            [hijo, prog],
            [f.handle],
          );
        }
      }
    }
  }

  // --- avisos informativos ----------------------------------------------
  for (const f of arbol.familias.values()) {
    const hijos = f.hijos.length;
    if (!f.padre && !f.madre && hijos < 2) {
      anade(
        "familia-sin-sentido",
        "aviso",
        `la familia ${f.id ?? f.handle} no tiene padre ni madre y ${hijos === 0 ? "ningún hijo" : "un solo hijo"}: no aporta ninguna relación`,
        [],
        [f.handle],
      );
    }
  }

  for (const h of ix.todas()) {
    if (!ix.familiasComoHijo(h).length && !ix.familiasComoProgenitor(h).length) {
      anade("persona-suelta", "info", `${nom(h)} no está conectada a ninguna familia`, [h]);
    }
  }

  return out;
}

/** Cuenta las incidencias por severidad. */
export function resumenIncidencias(xs: Incidencia[]): Record<Severidad, number> {
  const r: Record<Severidad, number> = { error: 0, aviso: 0, info: 0 };
  for (const x of xs) r[x.severidad]++;
  return r;
}
